import { AvailableCompanions } from "@hooks/use-companion";
import Twemoji from "@src/Twemoji";
import { useFlags } from "launchdarkly-react-client-sdk";

const renderCompanionEmoji = (type) => {
  const emoji = AvailableCompanions[type]?.emoji;
  return emoji ? <Twemoji emoji={emoji} /> : null;
};

const CompanionHelp = () => {
  return (
    <div className="flex flex-1 justify-between flex-col h-full">
      <div className="rounded-xl h-full bg-white flex flex-1 flex-col p-6 dark:bg-gray-800 dark:text-gray-300 gap-2 overflow-y-auto overscroll-contain">
        <h1 className="text-2xl font-bold">What is Companion?</h1>
        <p className="text-gray-500 dark:text-gray-300">
          Companions help you get more things done around Scribenote.
          They are powered by veterinary-trained AI and can help you
          with a variety of tasks to save you time.
        </p>
        <p className="text-gray-500 dark:text-gray-300">
          Companions are in beta, and AI can make mistakes. Remember
          to double-check any results!
        </p>
        <h1 className="text-2xl font-bold">
          Meet your Companion Team
        </h1>
        <HistoryCompanionInfo />
        <WritingCompanionInfo />
        <ResearchCompanionInfo />
        <MultiCompanionInfo />

        <p className="text-gray-500 dark:text-gray-300">
          Over time, we'll be introducing more 'Companions' to help
          you with more tasks.
        </p>
      </div>
    </div>
  );
};

const HistoryCompanionInfo = () => {
  return (
    <>
      <h2 className="text-xl font-semibold">
        {renderCompanionEmoji("SUMMI")} Summi: The History Companion
      </h2>
      <p className="text-gray-500 dark:text-gray-300">
        Summi is happy to help you summarize patient records, making
        it easier to understand the patient's history and get to
        caring for them faster and better. Simply upload the patient's
        records, and Summi will summarize them for you.
        <br />
        <br />
        You can also ask Summi questions about the patient's history,
        and she will answer them for you.
      </p>
    </>
  );
};

const ResearchCompanionInfo = () => {
  const { researchCompanion } = useFlags();
  if (!researchCompanion) {
    return null;
  }
  return (
    <>
      <h2 className="text-xl font-semibold">
        {renderCompanionEmoji("SAGE")} Sage: The Research Companion
      </h2>
      <p className="text-gray-500 dark:text-gray-300">
        Sage helps you answer tough questions by searching through
        high quality resources. Try asking questions like "What is
        paroxysmal dyskinesia?"
        <br />
        <br />
        Sage works slowly to find the most relevant information, so be
        patient!
      </p>
    </>
  );
};

const WritingCompanionInfo = () => {
  const { writingCompanion } = useFlags();
  if (!writingCompanion) {
    return null;
  }
  return (
    <>
      <h2 className="text-xl font-semibold">
        {renderCompanionEmoji("QUILL")} Quill: The Writing Companion
      </h2>
      <p className="text-gray-500 dark:text-gray-300">
        Quill helps you edit notes and client summaries using natural
        language. Ask for changes like "The plan is too detailed" or
        "Correct the misspelled medication names".
        <br />
        <br />
        Quill works best when you're viewing the note you want to edit
        (on Edit Note pages).
      </p>
    </>
  );
};

const MultiCompanionInfo = () => {
  const { writingCompanion, researchCompanion } = useFlags();
  if (!writingCompanion && !researchCompanion) {
    return null;
  }
  return (
    <>
      <h2 className="text-xl font-semibold">How to Use Companion</h2>
      <ul className="text-gray-500 dark:text-gray-300 text-base">
        <li>
          <b>@summi</b> - Direct a message to Summi
        </li>
        {writingCompanion && (
          <li>
            <b>@quill</b> - Direct a message to Quill
          </li>
        )}
        {researchCompanion && (
          <li>
            <b>@sage</b> - Direct a message to Sage
          </li>
        )}
        <li>
          <b>Companions work together </b>in the same chat session
        </li>
      </ul>
    </>
  );
};

export default CompanionHelp;
